import { cx } from "class-variance-authority";
import { DateTime as LDateTime } from "luxon";
import React from "react";
import { FiChevronRight } from "react-icons/fi";
import DateTime from "../datetime";
import { ExpandableReadonlyRichTextView } from "../rich-text-editor/toggleable-rich-text-view";

export type EmailFieldSet = {
  email: string;
};

export interface EventRegistrationProps {
  logo: string;
  orgName: string;
  title: string;
  description?: string;
  startTime?: luxon.DateTime;
  endTime?: luxon.DateTime;
  rangeType: "time" | "date";
  registrationDeadline?: luxon.DateTime;
  isRegistrationOpen: boolean;
  registrationMessage: React.ReactNode;
  registrationClosedMessage: React.ReactNode;
  children?: React.ReactNode;
  showTimeOnDateRange?: boolean;
}

const EventRegistration = ({
  logo,
  orgName,
  title,
  description,
  startTime,
  endTime,
  rangeType,
  registrationDeadline,
  isRegistrationOpen,
  registrationMessage,
  registrationClosedMessage,
  children,
  showTimeOnDateRange = false,
}: EventRegistrationProps) => {
  return (
    <>
      <div className="pt-12">
        <img
          src={logo}
          alt={`${orgName} logo`}
          className="mx-auto h-[88px] w-[88px] object-contain"
        />
      </div>
      <p className="text-medium-hippo font-inter pt-6 text-center text-base font-medium leading-5">
        {orgName} has invited you to
      </p>
      <h1 className="text-charcoal font-inter px-6 pt-3 text-center text-3xl font-black leading-8">
        {title}
      </h1>
      <div className="p-4 pb-6 sm:p-8">
        {description ? (
          <ExpandableReadonlyRichTextView
            content={description}
            cutoffParams={{ wrappingThreshold: 15, maxChars: 200 }}
            textClassName={cx(
              "font-inter text-base text-medium-hippo text-left",
            )}
          />
        ) : null}
      </div>
      <div className="space-y-6 px-4 pb-6 sm:px-8">
        <div className="border-tint-blue font-inter w-full space-y-4 rounded border-2 py-6 text-center">
          {rangeType === "time" ? (
            <TimeRange startTime={startTime} endTime={endTime} />
          ) : (
            <DateRange
              startTime={startTime}
              endTime={endTime}
              showTimeOnDateRange={showTimeOnDateRange}
            />
          )}
        </div>
      </div>
      {isRegistrationOpen ? (
        <div className="space-y-6 px-4 pb-6 sm:px-8">
          {!!registrationDeadline && registrationMessage}

          {children}
        </div>
      ) : (
        <div className="border-tint-blue border-t px-4 py-8">
          <p className="text-center font-semibold">
            {registrationClosedMessage}
          </p>
        </div>
      )}
    </>
  );
};

interface TimeRangeProps {
  startTime: LDateTime;
  endTime: LDateTime;
}
const TimeRange = (props: TimeRangeProps) => {
  return (
    <>
      <p className="text-medium-hippo text-lg">
        <DateTime date={props.startTime} format={"EEE dd MMM yyyy"} />
      </p>
      <div className="flex items-center justify-center">
        <div>
          <p className="text-charcoal text-3xl font-medium">
            <DateTime date={props.startTime} format={LDateTime.TIME_SIMPLE} />
          </p>
        </div>
        <div className="px-5">
          <FiChevronRight
            className="text-medium-hippo"
            size="20"
            aria-hidden="true"
          />
        </div>
        <div>
          <p className="text-charcoal text-3xl font-medium">
            <DateTime date={props.endTime} format={LDateTime.TIME_SIMPLE} />
          </p>
        </div>
      </div>
    </>
  );
};

interface DateRangeProps {
  startTime: LDateTime;
  endTime: LDateTime;
  showTimeOnDateRange: boolean;
}
const DateRange = (props: DateRangeProps) => {
  return (
    <div className="flex flex-row items-center justify-center gap-x-8">
      <div className="flex flex-col gap-y-2">
        <p className="font-inter text-medium-hippo text-center text-base font-normal">
          Start Date
        </p>
        <p className="font-inter text-charcoal flex flex-col text-2xl font-medium">
          <DateTime date={props.startTime} format={LDateTime.DATE_SHORT} />
          {props.showTimeOnDateRange ? (
            <DateTime
              date={props.startTime}
              format={LDateTime.TIME_SIMPLE}
              className="text-medium-hippo text-xl"
              showTooltip={false}
            />
          ) : null}
        </p>
      </div>
      <div>
        <FiChevronRight className="text-medium-hippo" size="20" />
      </div>
      <div className="flex flex-col gap-y-2">
        <p className="font-inter text-medium-hippo text-center text-base font-normal">
          End Date
        </p>
        <p className="font-inter text-charcoal flex flex-col text-2xl font-medium">
          <DateTime date={props.endTime} format={LDateTime.DATE_SHORT} />
          {props.showTimeOnDateRange ? (
            <DateTime
              date={props.endTime}
              format={LDateTime.TIME_SIMPLE}
              className="text-medium-hippo text-xl"
              showTooltip={false}
            />
          ) : null}
        </p>
      </div>
    </div>
  );
};

export default EventRegistration;
